import { Avatar } from 'antd';
import AvatarBox from 'components/AvatarBox';
import Text from 'components/Text';
import customerApis from 'features/customer/services/apis';
import { Bill, StaffItem } from 'features/ticketList/services/types/api';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { IResponseDataBody } from 'services/response';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import Box from 'components/Box';

type Props = {
  customerId: string;
};

const TransactionsList = ({ customerId }: Props) => {
  const [transactions, setTransactions] = useState<Bill[]>([]);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [transactionsLoading, setTransactionsLoading] = useState(false);

  useEffect(() => {
    if (customerId) {
      fetchTransactions();
    }
  }, [customerId]);

  const fetchTransactions = async () => {
    if (!customerId) return;
    setTransactionsLoading(true);
    try {
      const res: IResponseDataBody<{ content: Bill[]; totalElements: number }> =
        await customerApis.getTransactions({
          customerId,
          page: 1,
          size: 20,
        });
      if (res.data.data?.content) {
        setTransactions(res.data.data.content);
        setTotalTransactions(res.data.data.totalElements);
      }
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setTransactionsLoading(false);
    }
  };

  if (!customerId) return null;

  return (
    <TransactionsSection>
      <TransactionsTitle>
        Transactions <TransactionCount>({totalTransactions})</TransactionCount>
      </TransactionsTitle>
      {transactionsLoading ? (
        <div>Loading...</div>
      ) : (
        transactions.map((transaction, index) => (
          <TransactionCard key={transaction.billId || index}>
            <TransactionHeader>
              <TransactionLeft>
                <TransactionId>
                  #{transaction.ticketNumber || index + 1}
                </TransactionId>
                <TransactionInfo>
                  <TransactionDate>
                    {transaction.createdDate
                      ? moment(transaction.createdDate).format('MM-DD-YYYY')
                      : '--'}
                  </TransactionDate>
                  <TransactionTime>
                    {transaction.startTime
                      ? moment(transaction.startTime).format('hh:mm A')
                      : '--'}{' '}
                    -{' '}
                    {transaction.endTime
                      ? moment(transaction.endTime).format('hh:mm A')
                      : '--'}
                  </TransactionTime>
                </TransactionInfo>
              </TransactionLeft>
              <TransactionRight>
                <TransactionAmount>
                  + {formatCurrency(transaction.total || 0)}
                </TransactionAmount>
                {/* <TransactionStaff>
                  {transaction.items?.map((item: StaffItem) => (
                    <TransactionStaffAvatar key={item.staffId}>
                      <Avatar
                        src={item.avatar ? item.avatar : undefined}
                        size={32}
                      >
                        {item.staffName?.[0] || 'A'}
                      </Avatar>
                    </TransactionStaffAvatar>
                  ))}
                </TransactionStaff> */}
              </TransactionRight>
            </TransactionHeader>
              <TransactionServices>
                {transaction.items?.flatMap((item: any) =>
                  item.services.map((service: any) => (
                    <TractionServiceItem
                      key={`${item.id}-${service.id}`}
                    >
                      <Text variant='CAPTION_2'>{service.itemName}</Text>

                      <Box display='flex' alignItems='center' gap='1'>
                        <Avatar
                          src={item.avatar || undefined}
                          size={28}
                        >
                          {item.staffName?.[0] || 'A'}
                        </Avatar>
                        <Text variant='CAPTION_2'>{item.staffName}</Text>
                      </Box>
                    </TractionServiceItem>
                  ))
                )}
              </TransactionServices>
          </TransactionCard>
        ))
      )}
    </TransactionsSection>
  );
};

export default TransactionsList;

const TransactionsSection = styled.div`
  margin: 0 8px;
  padding: 16px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
`;

const TransactionHeader = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

const TransactionsTitle = styled.div`
  color: #1d2129;
  font-weight: 600;
  font-size: 18px;
  margin-bottom: 16px;
`;

const TransactionCount = styled.span`
  color: #d2464f;
`;

const TransactionCard = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  padding: 16px;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;
  overflow: hidden;
  box-sizing: border-box;
`;

const TransactionLeft = styled.div`
  display: flex;
  gap: 12px;
  flex: 1;
`;

const TransactionId = styled.div`
  background: #1D2129;
  color: #fff;
  padding: 12px 12px;
  border-radius: 6px;
  font-size: 20px;
  font-weight: 600;
  width: fit-content;
  height: fit-content;
`;

const TransactionInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const TransactionDate = styled.div`
  color: #1D2129;
  font-size: 16px;
  font-weight: 500;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const TransactionTime = styled.div`
  color: #1D2129;
  font-size: 16px;
  font-weight: 500;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const TransactionServices = styled.div`
  color: #1D2129;
  font-size: 16px;
  background: #F6F7FC;
  padding: 4px 12px;
  border-radius: 6px;
  width: 100%;
  margin-top: 8px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  box-sizing: border-box;
`;

const TransactionRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  margin-left: 16px;
`;

const TransactionAmount = styled.div`
  color: #0DA472;
  font-weight: 600;
  font-size: 16px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const TransactionStaff = styled.div`
  display: flex;
  gap: 0;
  margin-top: -10px;
  align-items: center;
  position: relative;
`;

const TransactionStaffAvatar = styled.div`
  position: relative;
  z-index: 1;

  &:nth-child(2) {
    margin-left: -12px;
    z-index: 2;
  }

  &:nth-child(3) {
    margin-left: -12px;
    z-index: 3;
  }

  &:nth-child(4) {
    margin-left: -12px;
    z-index: 4;
  }
`;

const TractionServiceItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 2px 0;
  border-bottom: 1px solid #e5e7eb;

  &:last-child  {
    border-bottom: none;
  }
`;